import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { useQuery } from "@apollo/client/react";
import { WORKFLOW_ID } from "../contexts/RunContext/constants";
import {
  GetMyRunsDocument,
  RunStatus,
  type GetMyRunsQuery,
} from "../services/__generated__/graphql";
import { runSettingsObjectToCanonicalJson } from "../utils/runSettings";
import {
  type ReceiptValidity,
  type ReceiptValidityByRunId,
  resolveSelectedRun,
  resolveSelectedRunId,
  sortCompletedWorkflowRuns,
  sortWorkflowRuns,
} from "./runSelectionState";

export type RunRow = GetMyRunsQuery["getMyRuns"][number];

export interface RunSelectionContextValue {
  selectedRun: RunRow | null;
  selectedRunId: string | null;
  setSelectedRunId: Dispatch<SetStateAction<string | null>>;
  sortedRuns: RunRow[];
  visibleRuns: RunRow[];
  receiptValidityByRunId: ReceiptValidityByRunId;
  reportReceiptValidity: (runId: string, validity: Exclude<ReceiptValidity, "unknown">) => void;
  loadingRuns: boolean;
  runSettingsJson: string;
}

const RunSelectionContext = createContext<RunSelectionContextValue | null>(null);

export function RunSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [receiptValidityByRunId, setReceiptValidityByRunId] = useState<
    Record<string, ReceiptValidity>
  >({});

  // cache-and-network: render cached runs immediately, then refresh in the background.
  // Subscribing via useQuery means cache writes from setMyRunName / setMyRunDescription
  // (see RunHistoryView.saveRunName / saveRunDescription) propagate here without a refetch.
  const { data, loading, error } = useQuery<GetMyRunsQuery>(GetMyRunsDocument, {
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (error) console.error("Failed to load runs", error);
  }, [error]);

  const visibleRuns = useMemo(
    () => sortWorkflowRuns(data?.getMyRuns ?? [], WORKFLOW_ID),
    [data?.getMyRuns]
  );
  const sortedRuns = useMemo(
    () => sortCompletedWorkflowRuns(visibleRuns, WORKFLOW_ID, RunStatus.RunCompleted),
    [visibleRuns]
  );

  const reportReceiptValidity = useCallback(
    (runId: string, validity: Exclude<ReceiptValidity, "unknown">) => {
      setReceiptValidityByRunId((current) =>
        current[runId] === validity ? current : { ...current, [runId]: validity }
      );
    },
    []
  );

  useEffect(() => {
    if (sortedRuns.length === 0) {
      return;
    }

    const resolvedRunId = resolveSelectedRunId(selectedRunId, sortedRuns);
    if (resolvedRunId !== selectedRunId) {
      setSelectedRunId(resolvedRunId);
    }
  }, [selectedRunId, sortedRuns]);

  const selectedRun = useMemo(
    () => resolveSelectedRun(selectedRunId, sortedRuns),
    [selectedRunId, sortedRuns]
  );

  const runSettingsJson = useMemo(
    () => (selectedRun ? runSettingsObjectToCanonicalJson(selectedRun.runSettings) : ""),
    [selectedRun]
  );

  const loadingRuns = loading && !data;

  const value = useMemo(
    () => ({
      selectedRun,
      selectedRunId,
      setSelectedRunId,
      sortedRuns,
      visibleRuns,
      receiptValidityByRunId,
      reportReceiptValidity,
      loadingRuns,
      runSettingsJson,
    }),
    [
      loadingRuns,
      receiptValidityByRunId,
      reportReceiptValidity,
      runSettingsJson,
      selectedRun,
      selectedRunId,
      sortedRuns,
      visibleRuns,
    ]
  );

  return createElement(RunSelectionContext.Provider, { value }, children);
}

export function useRunSelection(): RunSelectionContextValue {
  const context = useContext(RunSelectionContext);
  if (!context) {
    throw new Error("useRunSelection must be used within RunSelectionProvider");
  }
  return context;
}
